import { useState, useEffect } from "react";
import { Server, Wifi, WifiOff, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { vpsConnect, vpsDisconnect, vpsStatus } from "../../lib/api";
import { cn } from "../../lib/cn";

interface VpsState {
  connected: boolean;
  host?: string;
  user?: string;
  error?: string;
}

interface VpsPanelProps {
  autoShowForm?: boolean;
  onConnected?: () => void;
}

// LocalStorage key для сохранённых credentials (без пароля!).
const STORAGE_KEY = "aicores.vps.saved";

interface SavedCreds {
  host: string;
  user: string;
  port: string;
}

function loadSavedCreds(): SavedCreds | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedCreds;
    if (typeof parsed.host !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCreds(c: SavedCreds): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* localStorage может быть disabled — игнорируем */
  }
}

function clearSavedCreds(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function VpsPanel({ autoShowForm, onConnected }: VpsPanelProps = {}) {
  const [state, setState] = useState<VpsState>({ connected: false });
  const [loading, setLoading] = useState(false);
  // Pre-fill из localStorage при первом mount.
  const [form, setForm] = useState<{ host: string; user: string; password: string; port: string }>(
    () => {
      const saved = loadSavedCreds();
      return {
        host: saved?.host || "",
        user: saved?.user || "",
        password: "",
        port: saved?.port || "",
      };
    },
  );
  const [rememberMe, setRememberMe] = useState<boolean>(() => loadSavedCreds() !== null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showForm, setShowForm] = useState(autoShowForm || false);

  // На первом mount — проверяем status, чтобы не показывать форму если уже подключён.
  useEffect(() => {
    void checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function checkStatus() {
    setLoading(true);
    try {
      const s = (await vpsStatus()) as Record<string, unknown>;
      const connected = Boolean(s.connected || s.active);
      setState({
        connected,
        host: String(s.host || ""),
        user: String(s.user || ""),
      });
    } catch {
      setState({ connected: false });
    } finally {
      setLoading(false);
    }
  }

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setState((s) => ({ ...s, error: undefined }));
    try {
      await vpsConnect({
        host: form.host,
        user: form.user,
        password: form.password || null,
        port: Number(form.port) || 22,
      });
      // Сохраняем credentials (без пароля) если включён checkbox.
      if (rememberMe) {
        saveCreds({ host: form.host, user: form.user, port: form.port });
      } else {
        clearSavedCreds();
      }
      setState({ connected: true, host: form.host, user: form.user });
      setShowForm(false);
      onConnected?.();
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : "Ошибка подключения",
      }));
    } finally {
      setLoading(false);
    }
  }

  async function handleDisconnect() {
    setLoading(true);
    try {
      await vpsDisconnect();
      setState({ connected: false });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Status card */}
      <div className="rounded-card border border-border/60 p-4 flex items-center gap-3">
        <div
          className={cn(
            "size-9 rounded-card grid place-items-center",
            state.connected ? "bg-green-500/15 text-green-400" : "bg-bg-subtle text-fg-muted",
          )}
        >
          {loading ? (
            <Loader2 className="size-5 animate-spin" />
          ) : state.connected ? (
            <Wifi className="size-5" />
          ) : (
            <WifiOff className="size-5" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-small font-medium">
            {state.connected ? `Подключён: ${state.user}@${state.host}` : "VPS не подключён"}
          </p>
          {state.error && <p className="text-small text-red-400 truncate">{state.error}</p>}
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost text-small" onClick={checkStatus} disabled={loading}>
            Обновить
          </button>
          {state.connected ? (
            <button className="btn-ghost text-small text-red-400" onClick={handleDisconnect}>
              Отключить
            </button>
          ) : (
            <button className="btn-ghost text-small" onClick={() => setShowForm(!showForm)}>
              {showForm ? "Закрыть" : "Подключить"}
            </button>
          )}
        </div>
      </div>

      {/* Connect form */}
      {showForm && !state.connected && (
        <form
          onSubmit={handleConnect}
          className="rounded-card border border-border/60 p-4 space-y-3"
        >
          <h3 className="text-small font-medium flex items-center gap-2">
            <Server className="size-4 text-accent-400" /> Подключение к VPS
          </h3>

          {/* Host */}
          <div>
            <label className="block text-small text-fg-muted mb-1">Хост / IP</label>
            <input
              type="text"
              required
              value={form.host}
              onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
              placeholder="например, 192.0.2.10 или example.com"
              className="w-full px-3 py-2 rounded-card border border-border/60 bg-bg-subtle text-fg text-small focus:outline-none focus:ring-2 focus:ring-accent-500"
              autoComplete="off"
            />
          </div>

          {/* User */}
          <div>
            <label className="block text-small text-fg-muted mb-1">Пользователь</label>
            <input
              type="text"
              required
              value={form.user}
              onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
              placeholder="имя SSH-пользователя"
              className="w-full px-3 py-2 rounded-card border border-border/60 bg-bg-subtle text-fg text-small focus:outline-none focus:ring-2 focus:ring-accent-500"
              autoComplete="off"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-small text-fg-muted mb-1">Пароль</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="••••••••"
              className="w-full px-3 py-2 rounded-card border border-border/60 bg-bg-subtle text-fg text-small focus:outline-none focus:ring-2 focus:ring-accent-500"
              autoComplete="new-password"
            />
            <p className="text-[0.7rem] text-fg-subtle mt-1">
              Пароль никогда не сохраняется в браузере.
            </p>
          </div>

          {/* Advanced (collapsible) — порт */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-small text-fg-muted hover:text-fg flex items-center gap-1"
            >
              {showAdvanced ? (
                <ChevronUp className="size-3.5" />
              ) : (
                <ChevronDown className="size-3.5" />
              )}
              Дополнительно
            </button>
            {showAdvanced && (
              <div className="mt-2">
                <label className="block text-small text-fg-muted mb-1">Порт SSH</label>
                <input
                  type="text"
                  value={form.port}
                  onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
                  placeholder="22 (по умолчанию)"
                  className="w-full px-3 py-2 rounded-card border border-border/60 bg-bg-subtle text-fg text-small focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>
            )}
          </div>

          {/* Save for next login */}
          <label className="flex items-center gap-2 text-small text-fg-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="size-4 rounded border-border/60 bg-bg-subtle text-accent-500 focus:ring-2 focus:ring-accent-500"
            />
            Сохранить для следующего входа
            <span className="text-[0.7rem] text-fg-subtle">(хост, пользователь, порт — без пароля)</span>
          </label>

          <button
            type="submit"
            disabled={loading || !form.host.trim() || !form.user.trim()}
            className="w-full py-2 rounded-card bg-accent-600 hover:bg-accent-700 text-white text-small font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Подключение…" : "Подключить"}
          </button>
        </form>
      )}
    </div>
  );
}
